import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../Button";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
    EditEmissionsSourceFormValues,
    editEmissionsSourceValidationFields,
} from "./EditEmissionsSourceFormValues";
import {
    useEmissionsSourceItems,
    useUniqueReferenceIsUnique,
} from "../../api/emissions-sources";
import FormTextInput from "../FormTextInput";
import FormDropdown from "../FormDropdown";
import { useLocations } from "../../api/location";
import EmissionsSourceItemDetail from "../EmissionsSourceItemDetailForm";

type EditEmissionsSourceProps = {
    onSubmit: (values: EditEmissionsSourceFormValues) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    initialValues: Partial<EditEmissionsSourceFormValues>;
    id: number;
};

const EditEmissionsSourceForm = ({
    onSubmit,
    onCancel,
    isSubmitting,
    initialValues,
    id,
}: EditEmissionsSourceProps) => {
    const uniqueReferenceIsUnique = useUniqueReferenceIsUnique();

    const validationSchema: yup.SchemaOf<EditEmissionsSourceFormValues> =
        yup.object(
            editEmissionsSourceValidationFields(uniqueReferenceIsUnique, id)
        );

    const methods = useForm<EditEmissionsSourceFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(validationSchema),
        defaultValues: { ...initialValues },
    });

    const { locations, isLoading: isLoadingLocations } = useLocations();

    const locationOptions = locations
        ? locations.map((location) => ({
              value: location.id,
              label: location.name,
          }))
        : [];

    const { emissionsSourceItems, isLoading: isLoadingEmissionsSourceItems } =
        useEmissionsSourceItems();

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(onSubmit)}
                className="space-y-4 max-w-xl rounded-xl bg-surface-light-1 p-6 mx-auto"
            >
                <FormTextInput
                    required
                    label="Name"
                    placeholder="Enter emissions source name"
                    {...methods.register("name")}
                    error={methods.formState.errors.name}
                />
                <FormTextInput
                    required
                    label="Unique reference"
                    placeholder="Enter unique reference"
                    {...methods.register("uniqueReference")}
                    error={methods.formState.errors.uniqueReference}
                />
                <Controller
                    name="locationId"
                    control={methods.control}
                    render={({ field: { onChange, value } }) => (
                        <FormDropdown
                            required
                            isLoadingOptions={isLoadingLocations}
                            label="Location"
                            pleaseSelectText="Select location"
                            options={locationOptions}
                            value={value}
                            onChange={onChange}
                            error={methods.formState.errors.locationId}
                        />
                    )}
                />
                <FormTextInput
                    required
                    label="Justification"
                    placeholder="Enter justification"
                    {...methods.register("justification")}
                    error={methods.formState.errors.justification}
                />
                <EmissionsSourceItemDetail
                    emissionsSourceItems={emissionsSourceItems}
                    isLoadingEmissionsSourceItems={
                        isLoadingEmissionsSourceItems
                    }
                />
                <div className="flex justify-end gap-6 pt-4">
                    <Button
                        variant="transparent"
                        type="button"
                        disabled={isSubmitting}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" loading={isSubmitting}>
                        Save
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default EditEmissionsSourceForm;
